import { Typography, Box, LinearProgress, Divider, Card, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { formatCurrencyShort } from "../../../utils/format";
import { useDashboard } from "../../../contexts/DashboardContext";
import { CollaboratorBarsSkeleton } from "./Skeletons";
import { DashboardSourceInfo } from "./DashboardSourceInfo";

interface MetricsByCollaboratorProps {
  dashboardType?: "Supervisor" | "Gerente" | "CEO";
}

const StyledPaper = styled(Card)<{ dashboardtype?: string }>(({ theme, dashboardtype }) => ({
  padding: theme.spacing(2),
  height: dashboardtype === "Supervisor" ? 282 : 612,
}));

const ChartTitle = styled(Typography)({
  fontSize: "0.875rem",
  fontWeight: 500,
});

const MetricRow = styled(Box)({
  display: "flex",
  gap: "0.5rem",
  marginBottom: "0.75rem",
  paddingRight: 8,
  position: "relative",
});

const MetricName = styled(Typography)({
  fontSize: "0.75rem",
  color: "#3E3E3E",
  width: 120,
  textOverflow: "ellipsis",
  overflow: "hidden",
  whiteSpace: "nowrap",
});

const MetricValue = styled(Typography)({
  fontSize: "0.675rem",
  color: "#222222",
  minWidth: 60,
  textAlign: "right",
});

const StyledLinearProgress = styled(LinearProgress)({
  height: 16,
  borderRadius: 4,
  flex: 1,
  "& .MuiLinearProgress-bar": {
    borderRadius: 4,
  },
});

export const MetricsByCollaborator = ({ dashboardType = "Gerente" }: MetricsByCollaboratorProps) => {
  const {
    initialEventControlRankings,
    filteredEventControlRankings,
    loading,
    currentFilters,
    toggleCollaboratorFilter,
    hasFilters,
  } = useDashboard();

  const effectiveInitial = initialEventControlRankings ?? filteredEventControlRankings;
  const initialData = effectiveInitial?.revenueByCollaborator ?? [];
  const filteredData = hasFilters
    ? (filteredEventControlRankings?.revenueByCollaborator ?? [])
    : initialData;

  const combinedData = initialData.map((item) => {
    const filtered = filteredData.find((f) => f.id === item.id);
    return {
      collaboratorId: item.id,
      collaboratorName: item.name,
      filteredValue: filtered?.value ?? 0,
      filteredPercentage: filtered?.percentage ?? 0,
    };
  });

  // Define a altura máxima do container de itens com base no tipo de dashboard
  const maxHeight = dashboardType === "Supervisor" ? 240 : 568;

  return (
    <StyledPaper dashboardtype={dashboardType}>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <ChartTitle>Receita prevista por colaborador</ChartTitle>
          <DashboardSourceInfo context="Receita por colaborador" />
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <CollaboratorBarsSkeleton />
      ) : (
        <Box sx={{ overflowY: "auto", maxHeight }}>
          {combinedData
            .sort((a, b) => b.filteredValue - a.filteredValue)
            .map((item) => (
              <MetricRow
                key={item.collaboratorId}
                onClick={() => toggleCollaboratorFilter(item.collaboratorId)}
                sx={{
                  cursor: "pointer",
                  bgcolor:
                    currentFilters.collaboratorId === item.collaboratorId
                      ? "#142A3D10"
                      : "transparent",
                  "&:hover": {
                    bgcolor: "#142A3D20",
                  },
                }}
              >
                <MetricName>{item.collaboratorName}</MetricName>

                <Box position="relative" flex={1}>
                  <StyledLinearProgress
                    variant="determinate"
                    value={item.filteredPercentage}
                    sx={{
                      backgroundColor: hasFilters ? "#142A3D20" : "transparent",
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: "#142A3D",
                      },
                    }}
                  />
                </Box>

                <MetricValue>{formatCurrencyShort(item.filteredValue)}</MetricValue>
              </MetricRow>
            ))}
        </Box>
      )}
    </StyledPaper>
  );
};
